import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider
} from '@mui/material'
import {
  Save,
  Cancel,
  Archive as ArchiveIcon,
  Info
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { format } from 'date-fns'
import archiveService from '../../../services/archiveService'
import mrdService from '../../../services/mrdservice'

const ArchiveForm = ({ fileId, onSuccess, onCancel }) => {
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [mrdFile, setMrdFile] = useState(null)
  const [formData, setFormData] = useState({
    archiveType: 'PHYSICAL',
    location: {
      rack: '',
      shelf: '',
      box: '',
      position: ''
    },
    retentionPeriod: 10,
    accessLevel: 'CONFIDENTIAL',
    digitalPath: '',
    backupLocation: '',
    remarks: ''
  })

  useEffect(() => {
    if (fileId) {
      loadFileData()
    }
  }, [fileId])

  const loadFileData = async () => {
    try {
      const response = await mrdService.getFileById(fileId)
      if (response.data.success) {
        setMrdFile(response.data.data)
      }
    } catch (error) {
      enqueueSnackbar('Failed to load file data', { variant: 'error' })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validation
    if (!formData.location.rack || !formData.location.shelf) {
      enqueueSnackbar('Please specify archive location (rack and shelf)', { variant: 'error' })
      return
    }

    setLoading(true)
    try {
      const archiveData = {
        archiveType: formData.archiveType,
        location: formData.location,
        retentionPeriod: formData.retentionPeriod,
        accessLevel: formData.accessLevel,
        digitalPath: formData.digitalPath,
        backupLocation: formData.backupLocation,
        remarks: formData.remarks
      }
      
      const response = await archiveService.archiveFile(fileId, archiveData)
      
      if (response.data.success) {
        enqueueSnackbar('File archived successfully', { variant: 'success' })
        if (onSuccess) onSuccess(response.data.data)
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to archive file'
      enqueueSnackbar(errorMessage, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const archiveTypes = [
    { value: 'PHYSICAL', label: 'Physical Storage' },
    { value: 'DIGITAL', label: 'Digital Archive' },
    { value: 'MICROFILM', label: 'Microfilm' }
  ]

  const accessLevels = [
    { value: 'RESTRICTED', label: 'Restricted Access' },
    { value: 'CONFIDENTIAL', label: 'Confidential' },
    { value: 'GENERAL', label: 'General Access' }
  ]

  const calculateDestructionDate = () => {
    if (!mrdFile?.dischargeDate || !formData.retentionPeriod) return null
    
    const destructionDate = new Date(mrdFile.dischargeDate)
    destructionDate.setFullYear(destructionDate.getFullYear() + formData.retentionPeriod)
    return destructionDate
  }

  const destructionDate = calculateDestructionDate()

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {/* File Information */}
        {mrdFile && (
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="File Information" 
                avatar={<Info color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      IPD Number
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {mrdFile.ipdNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Patient Name
                    </Typography>
                    <Typography variant="body1">
                      {mrdFile.patientName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Discharge Date
                    </Typography>
                    <Typography variant="body1">
                      {mrdFile.dischargeDate ? format(new Date(mrdFile.dischargeDate), 'MMM dd, yyyy') : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Current Status
                    </Typography>
                    <Chip
                      label={mrdFile.fileStatus}
                      color={mrdFile.fileStatus === 'COMPLETED' ? 'success' : 'default'}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Archive Details */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Archive Configuration" />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Archive Type</InputLabel>
                    <Select
                      value={formData.archiveType}
                      label="Archive Type"
                      onChange={(e) => setFormData(prev => ({ ...prev, archiveType: e.target.value }))}
                    >
                      {archiveTypes.map(type => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Access Level</InputLabel>
                    <Select
                      value={formData.accessLevel}
                      label="Access Level"
                      onChange={(e) => setFormData(prev => ({ ...prev, accessLevel: e.target.value }))}
                    >
                      {accessLevels.map(level => (
                        <MenuItem key={level.value} value={level.value}>
                          {level.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Retention Period (Years)"
                    value={formData.retentionPeriod}
                    onChange={(e) => setFormData(prev => ({ ...prev, retentionPeriod: parseInt(e.target.value) }))}
                    inputProps={{ min: 1, max: 50 }}
                  />
                </Grid>

                {destructionDate && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <Typography variant="body2">
                        <strong>Scheduled Destruction Date:</strong> {format(destructionDate, 'MMMM dd, yyyy')}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Location Details */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Storage Location" />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Rack *"
                    value={formData.location.rack}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      location: { ...prev.location, rack: e.target.value }
                    }))}
                    placeholder="e.g., R-001"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Shelf *"
                    value={formData.location.shelf}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      location: { ...prev.location, shelf: e.target.value }
                    }))}
                    placeholder="e.g., S-05"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Box"
                    value={formData.location.box}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      location: { ...prev.location, box: e.target.value }
                    }))}
                    placeholder="e.g., BOX-123"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Position"
                    value={formData.location.position}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      location: { ...prev.location, position: e.target.value }
                    }))}
                    placeholder="e.g., P-01"
                  />
                </Grid>

                {formData.archiveType === 'DIGITAL' && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Digital Storage Details
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Digital Path"
                        value={formData.digitalPath}
                        onChange={(e) => setFormData(prev => ({ ...prev, digitalPath: e.target.value }))}
                        placeholder="/archives/2024/medical-records/"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Backup Location"
                        value={formData.backupLocation}
                        onChange={(e) => setFormData(prev => ({ ...prev, backupLocation: e.target.value }))}
                        placeholder="Cloud backup or secondary storage location"
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Additional Information */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Additional Information" />
            <CardContent>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Remarks"
                value={formData.remarks}
                onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                placeholder="Any additional notes about this archive..."
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          startIcon={<Cancel />}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={<ArchiveIcon />}
        >
          Archive File
        </Button>
      </Box>
    </Box>
  )
}

export default ArchiveForm